import cx from "classnames";
import { Button as NewButton } from "components/DesignSystem/Button/Button";
import { useContext, useEffect, useState } from "react";
import { Card, Form } from "react-bootstrap";
import classNames from "classnames";
import { PlusIcon } from "components/icons/PlusIcon";
import { TrashSimple } from "components/icons/TrashSimple";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import authContext from "jwt_context&axios/authContext";
import {
  DirectorsSection,
  RepeatableField,
  Section,
  SectionField,
} from "types/Models/Filing";
import { FormDataConfiguration, Task } from "types/Models/task";
import { ReviewFormControl } from "./ReviewFormControl";

dayjs.extend(customParseFormat);

// Section-field descriptions (show_description_before / show_description_after)
// are backend HTML injected via dangerouslySetInnerHTML. Some carry an "OR"
// divider built from <hr> tags with inline black 2px styling, which renders as
// an ugly thick black line. These classes override any child <hr> to a thin
// neutral divider and lowercase the divider label ("OR" -> "or"), matching the
// design. Scoped to the description wrapper so prose descriptions are untouched.
export const FILING_DESCRIPTION_DIVIDER_CLASS =
  "[&_hr]:!t-h-px [&_hr]:!t-border-none [&_hr]:!t-bg-neutral-10 [&_hr+div]:t-lowercase sm:[&_ul]:t-pl-5";

type RadioValues = Record<string, string | null>;

type SharedFieldProps = {
  groupId: string;
  updatedTask: Task;
  formId: string;
  isArchived: boolean;
  section: Section | DirectorsSection;
  entityId: string;
  formData: FormDataConfiguration;
  season: string;
  deadline: string;
  entityName?: string;
  radioValues: RadioValues;
};

type FieldsProps = SharedFieldProps & {
  fields: SectionField[] | undefined;
  hide?: (checked: boolean) => void;
  hidden?: boolean;
  repeatableId?: string;
  sectionKey: string;
};

type RepeatableFieldsProps = SharedFieldProps & {
  repeatableFields: RepeatableField[];
  duplicationAllowed: boolean;
  title?: string;
  hidden: boolean;
  updateSectionKeyIdPair: (params: { key: string; id: number }) => void;
  updateRemovedSectionIds: (params: { key: string; id: number }) => void;
  sectionKey: string;
};

type SectionReviewProps = {
  section: Section | DirectorsSection;
  updateSectionKeyIdPair: (params: { key: string; id: number }) => void;
  updateRemovedSectionIds: (params: { key: string; id: number }) => void;
  groupId: string;
  updatedTask: Task;
  formId: string;
  isArchived: boolean;
  entityId: string;
  formData: FormDataConfiguration;
  season: string;
  deadline: string;
  entityName?: string;
};

const Fields = ({
  fields,
  hidden,
  repeatableId,
  groupId,
  updatedTask,
  formId,
  sectionKey,
  formData,
  section,
  entityId,
  season,
  deadline,
  radioValues,
  entityName,
}: FieldsProps) => {
  const { authtoken } = useContext(authContext);
  const { isCustomer } = useRoleBasedView();
  const cpa_user = authtoken.is_service_user;

  let selectedCurrency = "USD";
  fields?.forEach((field) => {
    if (field?.field_key?.includes("currency") && field.value) {
      selectedCurrency = field.value.split(" - ")?.[0] ?? "USD";
    }
  });

  if (!fields) {
    return null;
  }

  return fields?.map((field, index) => {
    const nextColumnField = fields[index + 1];
    const showDescriptionBefore =
      field?.show_description_before ||
      (nextColumnField?.show_description_before && field.is_first_column);
    const showDescriptionAfter =
      field?.show_description_after ||
      (nextColumnField?.show_description_after && field.is_first_column);

    const fieldRadio = field?.radio;
    const hideField =
      fieldRadio && Object.keys(fieldRadio).length > 0
        ? !Object.keys(fieldRadio).some(
            (key) => radioValues[key] === fieldRadio[key]
          )
        : false;

    if (
      field.is_second_column ||
      hideField ||
      field.should_hide_section_field
    ) {
      return null;
    }

    return (
      (!hidden || field.collapse_section) && (
        <>
          {showDescriptionBefore && (
            <span
              className={FILING_DESCRIPTION_DIVIDER_CLASS}
              dangerouslySetInnerHTML={{
                __html: showDescriptionBefore,
              }}
            />
          )}
          <Form.Group
            className={cx({
              "opacity-50": field.is_hidden_for_customer && isCustomer,
              "t-flex t-gap-4": field.is_first_column,
            })}
            key={field.field_key}
          >
            <div
              className={classNames("t-w-full", {
                "t-hidden": field.should_hide_section_field,
              })}
            >
              <ReviewFormControl
                authtoken={authtoken}
                sectionKey={sectionKey}
                formId={formId}
                updatedTask={updatedTask}
                disabled={
                  (!cpa_user && field.field_key === "grose_assets") ||
                  field.disabled
                }
                type={field.type}
                id={field.field_key}
                name={field.field_key}
                label={field.field_label}
                placeholder={field.placeholder}
                tooltip={field.tooltip}
                defaultValue={field.value}
                descript={field?.description}
                mandatory={field?.required}
                groupId={groupId}
                autofillKey={field.autofill_key}
                autofillPrefix={field.autofill_prefix}
                autofillSuffix={repeatableId}
                tag={field.tag}
                document={field.document}
                field={field}
                formData={formData}
                section={section}
                entityId={entityId}
                fields={fields}
                season={season}
                deadline={deadline}
                selectedCurrency={selectedCurrency}
                entityName={entityName}
                options={field.options}
              />
            </div>

            {field.is_first_column && nextColumnField?.is_second_column && (
              <div className="t-w-full">
                <ReviewFormControl
                  authtoken={authtoken}
                  sectionKey={sectionKey}
                  formId={formId}
                  updatedTask={updatedTask}
                  disabled={
                    (!cpa_user &&
                      nextColumnField.field_key === "grose_assets") ||
                    nextColumnField.disabled
                  }
                  type={nextColumnField.type}
                  id={nextColumnField.field_key}
                  name={nextColumnField.field_key}
                  label={nextColumnField.field_label}
                  placeholder={nextColumnField.placeholder}
                  tooltip={nextColumnField.tooltip}
                  defaultValue={nextColumnField.value}
                  descript={nextColumnField?.description}
                  mandatory={nextColumnField?.required}
                  groupId={groupId}
                  autofillKey={nextColumnField.autofill_key}
                  autofillPrefix={nextColumnField.autofill_prefix}
                  autofillSuffix={repeatableId}
                  tag={nextColumnField.tag}
                  document={nextColumnField.document}
                  field={nextColumnField}
                  formData={formData}
                  section={section}
                  entityId={entityId}
                  fields={fields}
                  deadline={deadline}
                  season={season}
                  selectedCurrency={selectedCurrency}
                  entityName={entityName}
                  options={field.options}
                />
              </div>
            )}
          </Form.Group>
          {showDescriptionAfter && (
            <span
              className={FILING_DESCRIPTION_DIVIDER_CLASS}
              dangerouslySetInnerHTML={{
                __html: showDescriptionAfter,
              }}
            />
          )}
        </>
      )
    );
  });
};

const RepeatableFields = ({
  repeatableFields,
  duplicationAllowed,
  title,
  hidden,
  updateSectionKeyIdPair,
  sectionKey,
  updateRemovedSectionIds,
  groupId,
  updatedTask,
  formId,
  isArchived,
  section,
  entityId,
  formData,
  season,
  deadline,
  radioValues,
  entityName,
}: RepeatableFieldsProps) => {
  const updatedSection = { ...section, repeatable_fields: repeatableFields };

  if (hidden) {
    return null;
  }

  return (
    <>
      {repeatableFields?.map(({ fields, id, group_header }, index) => (
        <Card key={id} className="mb-4">
          <Card.Body>
            <div className="t-px-2 t-mb-2 t-text-text-100 t-text-subtext">
              {group_header || section.section_title} {index + 1}
            </div>
            <div className="t-flex t-flex-col t-gap-y-6">
              <Fields
                formId={formId}
                updatedTask={updatedTask}
                fields={fields}
                repeatableId={id.toString()}
                groupId={groupId}
                sectionKey={sectionKey}
                isArchived={isArchived}
                formData={formData}
                section={updatedSection}
                entityId={entityId}
                season={season}
                deadline={deadline}
                radioValues={radioValues}
                entityName={entityName}
              />
            </div>
          </Card.Body>
        </Card>
      ))}
    </>
  );
};

export const FilingSectionReview = ({
  section,
  updateSectionKeyIdPair,
  updateRemovedSectionIds,
  groupId,
  updatedTask,
  formId,
  isArchived,
  entityId,
  formData,
  season,
  deadline,
  entityName,
}: SectionReviewProps) => {
  const field = section?.section_fields?.[0];
  const [hidden, hide] = useState(
    field?.type === "checkbox" && field?.value === "true"
  );
  const [radioValues, setRadioValues] = useState<RadioValues>(() => {
    const radioData =
      section?.section_fields?.reduce<RadioValues>((acc, field) => {
        if (field.type === "radio") {
          acc[field.field_key] = field.value ?? null;
        }
        return acc;
      }, {}) ?? {};

    const radioData1: RadioValues = {};
    section?.repeatable_fields?.forEach(({ fields }) => {
      fields?.forEach((field) => {
        if (field.type === "radio") {
          radioData1[field.field_key] = field.value ?? null;
        }
      });
    });

    return { ...radioData, ...radioData1 };
  });

  const firstRepeatableRadio = section.repeatable_fields?.[0]?.radio;
  const hideRepeatableField = firstRepeatableRadio
    ? !Object.keys(firstRepeatableRadio).some(
        (key) => radioValues[key] === firstRepeatableRadio[key]
      )
    : false;

  const shouldHideRepeatableFields =
    "should_hide_repeatable_fields" in section &&
    section.should_hide_repeatable_fields;

  return (
    <div className="t-border t-rounded-lg t-border-solid t-border-neutral-10 t-px-6 t-py-4">
      <div className="t-border t-border-solid t-border-t-0 t-border-l-0 t-border-r-0 t-border-neutral-10 t-w-full t-text-text-100 t-text-subtext t-pb-4 t-mb-4">
        {section.section_title}
      </div>
      <div className="t-space-y-6">
        <Fields
          sectionKey={section.section_key}
          formId={formId}
          updatedTask={updatedTask}
          fields={section?.section_fields}
          hide={hide}
          hidden={hidden}
          groupId={groupId}
          isArchived={isArchived}
          formData={formData}
          section={section}
          entityId={entityId}
          season={season}
          deadline={deadline}
          radioValues={radioValues}
          entityName={entityName}
        />
        {shouldHideRepeatableFields || hideRepeatableField ? (
          <></>
        ) : (
          <RepeatableFields
            formId={formId}
            updatedTask={updatedTask}
            updateSectionKeyIdPair={updateSectionKeyIdPair}
            duplicationAllowed={section.section_duplication_allowed}
            sectionKey={section.section_key}
            repeatableFields={section.repeatable_fields ?? []}
            title={section?.section_title}
            hidden={hidden}
            updateRemovedSectionIds={updateRemovedSectionIds}
            groupId={groupId}
            isArchived={isArchived}
            section={section}
            entityId={entityId}
            formData={formData}
            season={season}
            deadline={deadline}
            radioValues={radioValues}
            entityName={entityName}
          />
        )}
      </div>
    </div>
  );
};
